import React from "react";
import { GridColDef } from "@mui/x-data-grid";
import { Box, Tooltip, Typography } from "@mui/material";
import { ArrowDropDownOutlined } from "@mui/icons-material";
import {
  DATE_FIELDS,
  FIELDS_DEPENDENT_ON_TRANSPORT_DOCUMENT,
  EDITABLE_FIELDS,
  autocompleteFieldConfig,
  staticAutocompleteFieldConfig,
} from "../../config";
import {
  fieldMap,
  SECTIONS,
  sortableFieldsMap,
} from "../../helpers/map-cargo-packing";
import {
  getTabletPackingActionsColumnWidth,
  getTabletPackingColumnWidth,
} from "../../helpers/packing-tablet-columns";
import {
  canEditPackingList,
  canModifyDeliveredPackingList,
} from "@pages/shippingView/helpers/tabs-mapping";
import Chips from "@atoms/chip";
import {
  AutocompleteFieldKey,
  CargoPackagingProps,
  StaticAutocompleteFieldKey,
} from "../../types";
import dayjs from "@helpers/dayjs";
import { ActionIcon } from "../actionIcon";
import { formatWeight } from "@helpers/formatWeight";

type PackingColumnsOptions = {
  isTablet?: boolean;
  pinnedColumn?: string | null;
};

const PackingColumns = (
  role: string,
  handleEdit: (row: CargoPackagingProps) => void,
  handleUnlock: (row: CargoPackagingProps) => void,
  visibleColumns: string[],
  options: PackingColumnsOptions = {},
): GridColDef[] => {
  const { isTablet = false, pinnedColumn = null } = options;
  const fieldOrder = new Map(visibleColumns.map((id, index) => [id, index]));

  const columns: GridColDef[] = SECTIONS.flatMap((section) => {
    const entries = Object.entries(section.fields);
    const filteredEntries = entries
      .filter(([fieldKey]) => visibleColumns.includes(fieldMap[fieldKey]))
      .sort(
        (a, b) =>
          (fieldOrder.get(fieldMap[a[0]]) ?? 999) -
          (fieldOrder.get(fieldMap[b[0]]) ?? 999),
      );
    const middleIndex = Math.floor(filteredEntries.length / 2);

    return filteredEntries.map(([fieldKey, fieldLabel], index) => {
      const field = fieldMap[fieldKey];
      const isPinned = isTablet && pinnedColumn === field;
      const isStatus = isTablet && field === "status_delivery";
      const categoryStripHeight = isTablet ? 20 : 28;

      return {
        field,
        headerName: fieldLabel,
        ...(isTablet
          ? { flex: 1, minWidth: getTabletPackingColumnWidth(field) }
          : { width: 180 }),
        filterable: false,
        disableColumnMenu: true,
        editable: EDITABLE_FIELDS.includes(fieldKey),
        sortable: !!sortableFieldsMap[field],
        sortField: sortableFieldsMap[field] || undefined,
        headerClassName:
          [
            isPinned ? "lite-tms-pinned-column-header" : "",
            isStatus ? "lite-tms-status-header" : "",
          ]
            .filter(Boolean)
            .join(" ") || undefined,
        cellClassName:
          [
            isPinned ? "lite-tms-pinned-column" : "",
            isStatus ? "lite-tms-status-cell" : "",
          ]
            .filter(Boolean)
            .join(" ") || undefined,
        renderHeader: () => (
          <Box
            height="100%"
            width="100%"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Box
              sx={{
                width: "100%",
                height: `${categoryStripHeight}px`,
                background: section?.color,
                position: "absolute",
                top: `-${categoryStripHeight}px`,
                left: "0",
                zIndex: 1,
                pointerEvents: "none",
                textAlign: "center",
              }}
            >
              {index === middleIndex && (
                <Typography
                  fontSize={isTablet ? "11px" : "14px"}
                  lineHeight={`${categoryStripHeight}px`}
                >
                  {section?.title}
                </Typography>
              )}
            </Box>
            <Box
              display="flex"
              alignItems="center"
              position="relative"
              zIndex={2}
            >
              {fieldLabel}
              {!!sortableFieldsMap[field] && <ArrowDropDownOutlined />}
            </Box>
          </Box>
        ),
        renderCell: (params) => {
          const cargoKey = fieldMap[fieldKey];
          let value = params.row[cargoKey as keyof typeof params.row];

          if (["NET_WEIGHT", "GROSS_WEIGHT"].includes(fieldKey)) {
            value = formatWeight(value as number | string | null | undefined);
          }

          if (["STATUS_FCR", "CUSTOMS_STATUS"].includes(fieldKey)) {
            return value === undefined ? "" : <Chips status={value} />;
          }
          if (fieldKey === "STATUS_DELIVERY") {
            const status = params.row.status_delivery;
            return status === undefined ? "" : <Chips status={status} />;
          }

          if (FIELDS_DEPENDENT_ON_TRANSPORT_DOCUMENT.includes(fieldKey)) {
            const transportDocument =
              params.row[fieldMap["TRANSPORT_DOCUMENT"]];
            value = transportDocument ? value : "";
          }

          if (DATE_FIELDS.includes(fieldKey)) {
            value = value ? dayjs(value).format("DD/MM/YYYY") : "";
          }

          if ((fieldKey as AutocompleteFieldKey) in autocompleteFieldConfig) {
            if (fieldKey === "DESTINATION") {
              const val = [
                value?.customer?.name,
                value?.delivery_address,
                value?.delivery_city?.name,
                value?.delivery_city?.province,
                value?.delivery_city?.country,
                value?.delivery_city?.post_code,
              ]
                .filter(Boolean)
                .join(", ");
              return (
                <Tooltip title={val} arrow>
                  <Box
                    sx={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: isTablet ? "100%" : 160,
                    }}
                  >
                    {val}
                  </Box>
                </Tooltip>
              );
            }

            return (
              <Tooltip title={value?.name} arrow>
                <Box
                  sx={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: isTablet ? "100%" : 160,
                  }}
                >
                  {value?.name}
                </Box>
              </Tooltip>
            );
          }

          if (
            (fieldKey as StaticAutocompleteFieldKey) in
            staticAutocompleteFieldConfig
          ) {
            value = value?.name ?? value;
          }

          const maxWidth = isTablet ? "100%" : 160;
          return value?.length > 10 ? (
            <Tooltip title={value} arrow>
              <Box
                sx={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth,
                }}
              >
                {value}
              </Box>
            </Tooltip>
          ) : (
            value
          );
        },
      };
    });
  });

  if (canEditPackingList(role)) {
    const actionsWidth = isTablet
      ? getTabletPackingActionsColumnWidth(role)
      : 70;

    if (!isTablet) {
      columns.push({
        field: "__filler__",
        headerName: "",
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        flex: 1,
      });
    }

    columns.push({
      field: "actions",
      headerName: "Actions",
      sortable: false,
      filterable: false,
      resizable: false,
      disableColumnMenu: true,
      cellClassName: "actions",
      headerClassName: "actions",
      ...(isTablet
        ? { width: actionsWidth, minWidth: actionsWidth, flex: 0 }
        : { width: actionsWidth }),
      renderCell: (params) => {
        const isDelivered = params.row.status_delivery === "DELIVERED";
        const isBooked = params.row.status_delivery === "BOOKED";
        const isLocked = isDelivered || isBooked;
        const canModifyDelivered = canModifyDeliveredPackingList(role);
        const icon = {
          unlock: canModifyDelivered && isLocked,
          edit: !isLocked || !canModifyDelivered,
        };

        return (
          <ActionIcon
            icon={icon}
            disabled={isLocked && !canModifyDelivered}
            onEdit={() => handleEdit(params.row)}
            onUnlock={() => handleUnlock(params.row)}
          />
        );
      },
    });
  }

  return columns;
};

export default PackingColumns;
